import { memo, useCallback, useMemo } from 'react';

import { Button, Flex, NumericOption, OptionGroup, Separator, SliderOption, Text } from '@/components/core';
import { DEFAULT_MAX_PAGES } from '@/constants';
import { useModelConfigStore } from '@/store';

import { toNumber, USER_SETTINGS_DEFAULT, UserSettings } from './utils';

const IModelSettings = () => {
  const metadata = useModelConfigStore((state) => state.metadata);
  const preprocessConfig = useModelConfigStore((state) => state.preprocessConfig);
  const updateMetadata = useModelConfigStore((state) => state.updateMetadata);
  const updatePreprocessConfig = useModelConfigStore((state) => state.updatePreprocessConfig);
  const updatePostprocessConfig = useModelConfigStore((state) => state.updatePostprocessConfig);

  const userSettings: UserSettings = useMemo(
    () => ({
      ...USER_SETTINGS_DEFAULT,
      ...(metadata?.['user:settings'] as UserSettings),
    }),
    [metadata]
  );

  const updateSetting = useCallback(
    (key: string, value: string | number) => {
      updateMetadata({ 'user:settings': { ...userSettings, [key]: value } });
    },
    [updateMetadata, userSettings]
  );

  const setNumPages = useCallback(
    (val: number) => {
      const n = toNumber(val, DEFAULT_MAX_PAGES);
      updatePreprocessConfig({ maxPages: n });
      updatePostprocessConfig({ parameters: { n }, strategy: 'BEST_N_PAGES' });
    },
    [updatePostprocessConfig, updatePreprocessConfig]
  );

  const onReset = useCallback(() => {
    const updates: UserSettings = { ...USER_SETTINGS_DEFAULT, use_few_shot_learning: undefined };
    updateMetadata({ 'user:settings': updates });
    setNumPages(3);
  }, [setNumPages, updateMetadata]);

  return (
    <Flex col className="p-md gap-md">
      <OptionGroup>
        <NumericOption
          label="Page limit"
          tooltip="The maximum number of pages to process in a single document."
          value={preprocessConfig?.maxPages ?? 3}
          onChange={setNumPages}
        />
      </OptionGroup>

      <OptionGroup>
        <SliderOption
          label="Temperature"
          tooltip="Lower values make the output more focused and deterministic, while higher values increase creativity."
          value={userSettings?.temperature}
          step={0.1}
          onChange={(val) => updateSetting('temperature', toNumber(val))}
        />

        <SliderOption
          label="Cross-validation limit"
          tooltip="A higher value means stricter cross-validation. A trade-off is required as strictuer cross-validation will also discard more predictions."
          value={userSettings.cross_validation_limit}
          step={0.05}
          minValue={0.0}
          maxValue={0.5}
          onChange={(val) => updateSetting('cross_validation_limit', toNumber(val))}
        />

        <SliderOption
          label="Anti-hallucination sensitivity"
          tooltip="A higher value increases the model's likeliness to categorize a prediction as a hallucination."
          value={userSettings.hallucination_tolerance}
          step={0.1}
          onChange={(val) => updateSetting('hallucination_tolerance', toNumber(val))}
        />
      </OptionGroup>

      <Separator orientation="horizontal" />

      <Flex>
        <Button variant="secondary" onPress={onReset}>
          <Text>Reset to defaults</Text>
        </Button>
      </Flex>
    </Flex>
  );
};

export const ModelSettings = memo(IModelSettings);
